import React from 'react';
import dayjs from 'dayjs';
import {
  COD_BUCKET_LABELS,
  COD_FINANCE_ISSUE_REASON_LABELS,
  codLegLabel,
  codMethodLabel,
  type CodBucket,
  type CodOrderDetail,
  type CodConfirmationProof,
  type CodOrderHistoryEntry,
} from '@/services/api/codOrders';
import { formatDiscrepancy, formatRupiah } from './CodBadges';
import CodProofThumb from './CodProofThumb';

const statusLabel = (status: string | null) =>
  status
    ? (COD_BUCKET_LABELS[status as CodBucket] ?? status)
    : COD_BUCKET_LABELS.in_delivery;

const fmtDate = (v: string) => dayjs(v).format('ddd, DD MMM YYYY');
const fmtTime = (v: string) => dayjs(v).format('HH:mm');

/** Icon per event kind — the shape carries the meaning before the text does. */
const ICON: Record<CodOrderHistoryEntry['eventType'], string> = {
  status_change: 'ri-arrow-right-circle-fill',
  confirm_leg_1: 'ri-checkbox-circle-fill',
  confirm_leg_2: 'ri-checkbox-circle-fill',
  correction: 'ri-edit-circle-fill',
  proof_added: 'ri-attachment-2',
  finance_matched: 'ri-links-line',
  confirm_leg_3: 'ri-shield-check-fill',
  finance_flagged: 'ri-error-warning-fill',
  finance_returned: 'ri-arrow-go-back-line',
  rider_contacted: 'ri-whatsapp-line',
};

const TITLE: Record<CodOrderHistoryEntry['eventType'], string> = {
  status_change: 'Perubahan status',
  confirm_leg_1: 'Leg 1 dikonfirmasi · Rider → PIC',
  confirm_leg_2: 'Leg 2 dikonfirmasi · PIC → Finance',
  correction: 'Koreksi',
  proof_added: 'Bukti ditambahkan',
  finance_matched: 'Dicocokkan dengan mutasi bank',
  confirm_leg_3: 'Leg 3 dikonfirmasi · Finance',
  finance_flagged: 'Ditandai bermasalah',
  finance_returned: 'Dikembalikan ke PIC',
  rider_contacted: 'Follow-up ke rider',
};

/** One label/value pair inside a timeline entry. */
const Pair: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex gap-1.5 text-xs">
    <span className="text-text-sub-600">{label}</span>
    <span className="text-text-sub-600">{children}</span>
  </div>
);

/**
 * What a correction changed, old → new.
 *
 * `snapshot` holds the values as they stood BEFORE the edit — the only place a
 * superseded money figure survives. Rendering it beside the current leg is what
 * turns that stored row into something a reviewer can actually check, which is
 * the entire reason the column exists.
 */
const CorrectionBody: React.FC<{
  entry: CodOrderHistoryEntry;
  detail: CodOrderDetail;
}> = ({ entry, detail }) => {
  const snap = entry.snapshot;
  const legType = snap?.legType ?? null;
  const current =
    legType === 'rider_to_pic'
      ? detail.legRiderToPic
      : legType === 'pic_to_finance'
        ? detail.legPicToFinance
        : null;

  const oldAmount = snap?.confirmedAmount ? Number(snap.confirmedAmount) : null;
  const amountChanged =
    oldAmount != null &&
    current != null &&
    Math.round(oldAmount) !== Math.round(current.confirmedAmount);

  const oldMethod = snap?.method ?? null;
  const methodChanged =
    oldMethod != null && current != null && oldMethod !== current.method;

  return (
    <div className="flex flex-col gap-1">
      {/* Which leg was corrected — read from the snapshot, since a correction
          event's type says only "correction". */}
      {legType && <Pair label="Leg">{codLegLabel(legType)}</Pair>}

      {amountChanged && (
        <Pair label="Jumlah">
          <span className="line-through text-text-sub-600">
            {formatRupiah(oldAmount)}
          </span>{' '}
          →{' '}
          <span className="font-medium">
            {formatRupiah(current.confirmedAmount)}
          </span>
        </Pair>
      )}
      {!amountChanged && oldAmount != null && (
        <Pair label="Jumlah">{formatRupiah(oldAmount)} (tidak berubah)</Pair>
      )}

      {methodChanged && (
        <Pair label="Metode">
          <span className="line-through text-text-sub-600">
            {codMethodLabel(oldMethod)}
          </span>{' '}
          →{' '}
          <span className="font-medium">{codMethodLabel(current.method)}</span>
        </Pair>
      )}

      <TextChange
        label="Catatan selisih"
        before={snap?.discrepancyNote}
        after={current?.discrepancyNote}
      />
      <TextChange label="Catatan" before={snap?.notes} after={current?.notes} />
    </div>
  );
};

/**
 * The attachments an entry is about, previewed inline.
 *
 * Thumbnails rather than the labelled links this printed first: `Bukti
 * Transfer` as a link does not answer "is there really a receipt behind this",
 * it moves the question to a new tab where it mostly stops being asked. The
 * detail panel's evidence block renders through the same component, so the two
 * places read the same way.
 *
 * Read off the entry's own `snapshot`, never the live leg: evidence keeps
 * arriving after a leg closes (`proof_added`, corrections), so the live list
 * would make a file attached next week look as though it was there when the
 * money moved.
 */
const ProofStrip: React.FC<{
  label: string;
  proofs: CodConfirmationProof[];
}> = ({ label, proofs }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-text-sub-600">{label}</span>
    <div className="flex flex-wrap items-start gap-2">
      {proofs.map((proof) => (
        <CodProofThumb key={proof.imageUrl} proof={proof} size="sm" />
      ))}
    </div>
  </div>
);

/**
 * A free-text field a correction changed, old → new.
 *
 * The old value comes off the snapshot, the new one off the live leg — the same
 * pairing the amount and method use. Notes were being dropped entirely, so a
 * correction whose whole point was fixing the wording of a discrepancy showed
 * as "Jumlah Rp 70.000 (tidak berubah)" and nothing else.
 */
const TextChange: React.FC<{
  label: string;
  before: string | null | undefined;
  after: string | null | undefined;
}> = ({ label, before, after }) => {
  const old = before?.trim() || null;
  const now = after?.trim() || null;
  if (!old && !now) return null;

  if (old && now && old !== now) {
    return (
      <Pair label={label}>
        <span className="text-text-sub-600 line-through">{old}</span> →{' '}
        <span className="font-medium">{now}</span>
      </Pair>
    );
  }
  // Added where there was none, or removed, or left alone — in all three the
  // surviving value is the whole story and an arrow would invent a change.
  if (!old) return <Pair label={`${label} (baru)`}>{now}</Pair>;
  if (!now) {
    return (
      <Pair label={`${label} (dihapus)`}>
        <span className="text-text-sub-600 line-through">{old}</span>
      </Pair>
    );
  }
  return <Pair label={label}>{now}</Pair>;
};

/**
 * What a `proof_added` entry says: which leg, and what was attached.
 *
 * The snapshot holds the entries that were ADDED, not the leg's whole list — a
 * reader scrolling the timeline wants "this added the Berita Acara", not a
 * repeat of everything attached so far. Who added it is the `changedBy` line
 * every entry already prints.
 */
const ProofAddedBody: React.FC<{ entry: CodOrderHistoryEntry }> = ({
  entry,
}) => {
  const snap = entry.snapshot;
  const added = snap?.proofFiles ?? [];

  return (
    <div className="flex flex-col gap-1.5">
      {snap?.legType && <Pair label="Leg">{codLegLabel(snap.legType)}</Pair>}
      {added.length > 0 && <ProofStrip label="Lampiran" proofs={added} />}
    </div>
  );
};

/**
 * Method + figures for a confirm event, read from the entry's OWN snapshot.
 *
 * Not from `detail.legRiderToPic` / `legPicToFinance`, which is what this did
 * first and is a bug: those hold the LIVE confirmation, so correcting a leg
 * retroactively changed what the original confirm entry claimed to have
 * recorded. A leg confirmed at Rp 150.000 with no discrepancy, then corrected
 * to Rp 100.000, showed the confirm entry as Rp 100.000 with a −Rp 50.000
 * discrepancy that did not exist at the time — while the correction entry
 * beneath it correctly said 150.000 → 100.000. An audit trail that changes
 * when you edit the thing it audits is worse than no audit trail.
 *
 * Rows written before the snapshot was added carry none; those fall back to
 * the live leg, which is the pre-fix behaviour and the only figure available.
 */
const ConfirmBody: React.FC<{
  entry: CodOrderHistoryEntry;
  detail: CodOrderDetail;
}> = ({ entry, detail }) => {
  const snap = entry.snapshot;
  const liveLeg =
    entry.eventType === 'confirm_leg_1'
      ? detail.legRiderToPic
      : detail.legPicToFinance;

  const method = snap?.method !== undefined ? snap.method : liveLeg?.method;
  const amount =
    snap?.confirmedAmount != null
      ? Number(snap.confirmedAmount)
      : liveLeg?.confirmedAmount;
  const discrepancy =
    snap?.discrepancyAmount != null
      ? Number(snap.discrepancyAmount)
      : liveLeg?.discrepancyAmount;

  if (amount == null) return null;

  return (
    <div className="flex flex-col gap-1">
      {/* Labels, never the stored enum. The raw value is precise for the audit
          row but meaningless on screen. A legacy delivery-service row has no
          method at all, and that absence is itself the fact. */}
      <Pair label="Metode">{codMethodLabel(method ?? null)}</Pair>
      <Pair label="Jumlah">{formatRupiah(amount)}</Pair>
      {discrepancy != null && Math.round(discrepancy) !== 0 && (
        <Pair label="Selisih">
          <span className="font-medium text-state-warning-dark">
            {formatDiscrepancy(discrepancy)}
          </span>
        </Pair>
      )}

      {/* Evidence can be attached at the confirm itself, so the entry has to
          say so — otherwise the only place a file shows up is a later
          `proof_added`, and a leg confirmed WITH its receipt looks unevidenced
          in the trail. An empty list is worth stating (Leg 1 may legitimately
          close with none); an entry written before the snapshot carried
          attachments has no list at all, and that is what this skips. */}
      {snap?.proofFiles != null &&
        (snap.proofFiles.length > 0 ? (
          <ProofStrip label="Lampiran" proofs={snap.proofFiles} />
        ) : (
          <Pair label="Lampiran">Tidak ada</Pair>
        ))}
    </div>
  );
};

/**
 * What a Leg 3 event says: Finance's verdict, and the money it was about.
 *
 * Read off the entry's own `snapshot`, never the live order. Both live columns
 * are transient — `clear` nulls the reason, and a returned deposit's row is
 * retired the moment the PIC deposits again — so a timeline reading them would
 * show today's state on an entry describing last week's decision, or nothing
 * at all once the deposit bounced twice.
 *
 * Before this the entries rendered as a bare title and an email. The reason and
 * the operator's own words existed, but only inside the audit `note`, which is
 * written for SQL (`Ditahan untuk investigasi · amount_mismatch · uangnya
 * kurang`) and is deliberately not put on screen.
 */
const FinanceIssueBody: React.FC<{ entry: CodOrderHistoryEntry }> = ({
  entry,
}) => {
  const snap = entry.snapshot;
  const reason = snap?.financeIssueReason ?? null;
  const note = snap?.financeIssueNote?.trim() || null;
  const bankRef = snap?.financeBankRef?.trim() || null;
  const amount = snap?.confirmedAmount ?? null;

  if (!reason && !note && !bankRef && amount === null) return null;

  return (
    <div className="flex flex-col gap-1.5">
      {reason && (
        <Pair label="Alasan">
          <span className="font-medium text-text-strong-950">
            {COD_FINANCE_ISSUE_REASON_LABELS[reason] ?? reason}
          </span>
        </Pair>
      )}
      {bankRef && (
        <Pair label="Referensi mutasi">
          <span className="font-mono text-text-strong-950">{bankRef}</span>
        </Pair>
      )}
      {/* The returned deposit's figure, frozen before its row was retired —
          without it the entry cannot say how much went back to the PIC. */}
      {amount !== null && (
        <Pair label="Jumlah setoran">{formatRupiah(Number(amount))}</Pair>
      )}
      {note && (
        <p className="whitespace-pre-line rounded-md bg-bg-weak-50 px-2.5 py-2 text-xs text-text-sub-600">
          {note}
        </p>
      )}
    </div>
  );
};

/**
 * The COD audit trail, newest first.
 *
 * Rendered from the entry's STRUCTURED fields, not from the audit `note`. The
 * note is written for someone reading the table in SQL — it carries raw enums
 * and unformatted decimals on purpose — and putting it on screen is how
 * `tribe_write_off · Rp 2500000 · selisih Rp -2960000.00` ended up in front of
 * an operator. The note is still shown where it says something the columns
 * cannot: a correction's reason.
 *
 * Rail layout matches the delivery-detail timeline (date column · icon +
 * dashed connector · content) so the two screens read the same way.
 */
const CodTimeline: React.FC<{
  entries: CodOrderHistoryEntry[];
  detail: CodOrderDetail;
}> = ({ entries, detail }) => {
  if (entries.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-text-sub-600">
        Belum ada riwayat untuk pesanan ini.
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      {entries.map((entry, index) => {
        const isFirst = index === 0;
        const isLast = index === entries.length - 1;
        return (
          <div key={entry.id} className="flex flex-row gap-3">
            <div className="flex min-w-[104px] flex-col items-end gap-0.5 pt-0.5">
              <p
                className={`text-right text-[13px] font-medium ${
                  isFirst ? 'text-text-strong-950' : 'text-text-sub-600'
                }`}
              >
                {fmtDate(entry.changedAt)}
              </p>
              <p className="text-right text-[13px] tabular-nums text-text-sub-600">
                {fmtTime(entry.changedAt)} WIB
              </p>
            </div>

            <div className="flex flex-col items-center">
              <i
                className={`${ICON[entry.eventType] ?? 'ri-circle-fill'} text-[20px] ${
                  isFirst ? 'text-accent' : 'text-text-sub-600'
                }`}
              />
              {/* `flex-1`, not `h-full`. The rail is a flex COLUMN whose own
                  height comes from its children, so `height: 100%` on a child
                  has nothing to resolve against and collapses to zero — the
                  connector simply does not draw. `flex-1` makes it take the
                  space the row's content leaves, which is exactly the gap it
                  needs to span. `min-h` keeps it visible on one-line entries. */}
              {!isLast && (
                <div className="my-1 min-h-[20px] w-0 flex-1 border-l border-dashed border-stroke-sub-300" />
              )}
            </div>

            <div className="mb-5 ml-1 flex w-full flex-col gap-1.5">
              <p className="text-sm font-medium text-text-strong-950">
                {TITLE[entry.eventType] ?? entry.eventType}
              </p>

              {entry.eventType === 'status_change' && (
                <p className="text-xs text-text-sub-600">
                  {statusLabel(entry.fromStatus)} →{' '}
                  <span className="font-medium text-text-strong-950">
                    {statusLabel(entry.toStatus)}
                  </span>
                </p>
              )}

              {(entry.eventType === 'confirm_leg_1' ||
                entry.eventType === 'confirm_leg_2') && (
                <ConfirmBody entry={entry} detail={detail} />
              )}

              {entry.eventType === 'correction' && (
                <CorrectionBody entry={entry} detail={detail} />
              )}

              {entry.eventType === 'proof_added' && (
                <ProofAddedBody entry={entry} />
              )}

              {(entry.eventType === 'finance_flagged' ||
                entry.eventType === 'finance_returned' ||
                entry.eventType === 'finance_matched' ||
                entry.eventType === 'confirm_leg_3') && (
                <FinanceIssueBody entry={entry} />
              )}

              {/* Only a correction's note adds anything the fields above do
                  not already say — it is the reason someone changed a figure. */}
              {entry.eventType === 'correction' && entry.note && (
                <p className="whitespace-pre-line rounded-md bg-bg-weak-50 px-2.5 py-2 text-xs text-text-sub-600">
                  {entry.note}
                </p>
              )}

              <p className="text-[11px] text-text-sub-600">
                {/* `SYSTEM` is the delivery-status listener, not a person. */}
                {entry.changedBy === 'SYSTEM'
                  ? 'Otomatis dari event delivery'
                  : entry.changedBy}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CodTimeline;
